import { Navigate } from 'react-router-dom';

import AdminReview from 'screens/admin-review';
import CollectionDashboard from 'screens/collections/dashboard';
import Hotcash from 'screens/collections/hotcash';
import InvoiceListPage from 'screens/collections/invoice';
import PackageTracker from 'screens/deliveries/package-tracker';
import FarmerGroup from 'screens/farmer-groups';
import FarmerGroupForm from 'screens/farmer-groups/components/form';
import SalesGroupProgress from 'screens/sales-group-progress';
import JrAgLeads from 'screens/sales/ags';
import SalesDealsCalendarView from 'screens/sales/deals-calendar';
import SalesPipeline from 'screens/sales/leads';
import SalesOverview from 'screens/sales/overview';
import SeasonOverview from 'screens/season-overview';
import MachineOwnerForm from 'screens/stakeholders/components/form/machine-owner';
import InvoiceUtils from 'screens/system-utils/invoice';
import PaymentUtils from 'screens/system-utils/payment';
import SalesUtils from 'screens/system-utils/sales';
import TaskManagement from 'screens/task-management';
import { PermissionKey } from 'types/rbac';
import Dashboard from '../screens/awd-dashboard';
import Pipes from '../screens/awd-maps';
import Farmers from '../screens/farmer';
import Performance from '../screens/performance-dashboard';
import PipeReview from '../screens/PipeReview';
import Stakeholders from '../screens/stakeholders';
import FarmerGroupLeaderForm from '../screens/stakeholders/components/form/farmer-group-leader/index';
import PaddyBuyerForm from '../screens/stakeholders/components/form/paddy-buyer';
import Users from '../screens/users';
import AgronomistViewContent from 'screens/task-management/contents/AgronomistViewContent';
import TaskViewContent from 'screens/task-management/contents/TaskViewContent';

export const sidebarRoutes = {
  index: '/',
  home: '/home',
  seasonOverview: '/season-overview',
  taskManagement: '/task-management',
  awdProgress: '/awd-progress',
  pipes: '/pipes',
  pipeReview: '/pipe-review',
  users: '/users',
  farmers: '/farmers',
  performance: '/performance',
  stakeholders: '/stakeholders',
  paddyBuyer: '/stakeholders/paddy-buyer',
  farmerGroupLeader: '/stakeholders/farmer-group-leader',
  machineOwner: '/stakeholders/machine-owner',
  farmerGroup: '/farmer-group',
  hotcash: '/collections/cash',
  collectionOverview: '/collections/overview',
  collectionInvoices: '/collections/invoices',
  salesLeads: '/sales/leads',
  salesAgData: '/sales/ag-data',
  salesWeeklyView: '/sales/weekly-view',
  salesCalendarView: '/sales/calendar-view',
  salesGroupProgress: '/sales/group-progress',
  salesOverview: '/sales/overview',
  deliveryPackages: '/delivery/packages',
  paymentUtils: '/system-utils/payments',
  invoiceUtils: '/system-utils/invoice',
  salesUtils: '/system-utils/sales',
  adminReview: '/admin-review',
};

export const routes = {
  ...sidebarRoutes,
  paddyBuyerCreate: '/stakeholders/paddy-buyer/create',
  paddyBuyerEdit: '/stakeholders/paddy-buyer/edit/:id',
  farmerGroupLeaderCreate: '/stakeholders/farmer-group-leader/create',
  farmerGroupLeaderEdit: '/stakeholders/farmer-group-leader/edit/:id',
  farmerGroupCreate: '/farmer-group/create',
  farmerGroupEdit: '/farmer-group/edit/:id',
  machineOwnerCreate: '/machine-owner/create',
  machineOwnerEdit: '/machine-owner/edit/:id',
  sales: '/sales',
  taskWeeklyView: '/task-management/weekly-view',
  taskAgronomistView: '/task-management/agronomist-view',
};

interface BaseRoute {
  key: keyof typeof routes;
  element: JSX.Element;
  authorizedPermissions?: PermissionKey[];
  children?: PrivateRoute[];
}

interface PathRoute extends BaseRoute {
  index?: false;
  path: string;
}

interface IndexRoute extends BaseRoute {
  index: true;
  path?: never;
}

export type PrivateRoute = PathRoute | IndexRoute;

export const privateRoutes: PrivateRoute[] = [
  {
    path: routes.index,
    key: 'index',
    element: <SeasonOverview />,
    authorizedPermissions: ['matrix:read'],
  },
  {
    path: routes.awdProgress,
    key: 'awdProgress',
    element: <Dashboard />,
    authorizedPermissions: ['mrv:read'],
  },
  {
    path: routes.seasonOverview,
    key: 'seasonOverview',
    element: <Navigate to="/" />,
    authorizedPermissions: ['matrix:read'],
  },
  {
    path: routes.taskManagement,
    key: 'taskManagement',
    element: <TaskManagement />,
    authorizedPermissions: ['task:read'],
    children: [
      {
        key: 'taskWeeklyView',
        element: <TaskViewContent />,
        authorizedPermissions: ['task:read'],
        index: true,
      },
      {
        path: routes.taskAgronomistView,
        key: 'taskAgronomistView',
        element: <AgronomistViewContent />,
        authorizedPermissions: ['task:read'],
      },
    ],
  },
  {
    path: routes.pipes,
    key: 'pipes',
    element: <Pipes />,
    authorizedPermissions: ['mrv:read'],
  },
  {
    path: routes.pipeReview,
    key: 'pipeReview',
    element: <PipeReview />,
    authorizedPermissions: ['mrv:review'],
  },
  {
    path: routes.users,
    key: 'users',
    element: <Users />,
    authorizedPermissions: ['user:read'],
  },
  {
    path: routes.farmers,
    key: 'farmers',
    element: <Farmers />,
    authorizedPermissions: ['farmer:read'],
  },
  {
    path: routes.performance,
    key: 'performance',
    element: <Performance />,
    authorizedPermissions: ['matrix:read'],
  },
  {
    path: routes.stakeholders,
    key: 'stakeholders',
    element: <Stakeholders />,
    authorizedPermissions: [
      'paddy_buyer:read',
      'farmer_group_leader:read',
      'machine_owner:read',
    ],
  },
  {
    path: routes.paddyBuyer,
    key: 'paddyBuyer',
    element: <Stakeholders />,
    authorizedPermissions: ['paddy_buyer:read'],
  },
  {
    path: routes.paddyBuyerCreate,
    key: 'paddyBuyerCreate',
    element: <PaddyBuyerForm />,
    authorizedPermissions: ['paddy_buyer:add'],
  },
  {
    path: routes.paddyBuyerEdit,
    key: 'paddyBuyerEdit',
    element: <PaddyBuyerForm edit />,
    authorizedPermissions: ['paddy_buyer:edit'],
  },
  {
    path: routes.farmerGroupLeader,
    key: 'farmerGroupLeader',
    element: <Stakeholders />,
    authorizedPermissions: ['farmer_group_leader:read'],
  },
  {
    path: routes.farmerGroupLeaderCreate,
    key: 'farmerGroupLeaderCreate',
    element: <FarmerGroupLeaderForm />,
    authorizedPermissions: ['farmer_group_leader:add'],
  },
  {
    path: routes.farmerGroupLeaderEdit,
    key: 'farmerGroupLeaderEdit',
    element: <FarmerGroupLeaderForm edit />,
    authorizedPermissions: ['farmer_group_leader:edit'],
  },
  {
    path: routes.machineOwner,
    key: 'machineOwner',
    element: <Stakeholders />,
    authorizedPermissions: ['machine_owner:read'],
  },
  {
    path: routes.machineOwnerCreate,
    key: 'machineOwnerCreate',
    element: <MachineOwnerForm />,
    authorizedPermissions: ['machine_owner:add'],
  },
  {
    path: routes.machineOwnerEdit,
    key: 'machineOwnerEdit',
    element: <MachineOwnerForm edit />,
    authorizedPermissions: ['machine_owner:edit'],
  },
  {
    path: routes.farmerGroup,
    key: 'farmerGroup',
    element: <FarmerGroup />,
    authorizedPermissions: ['farmer_group:read'],
  },
  {
    path: routes.farmerGroupCreate,
    key: 'farmerGroupCreate',
    element: <FarmerGroupForm />,
    authorizedPermissions: ['farmer_group:add'],
  },
  {
    path: routes.farmerGroupEdit,
    key: 'farmerGroupEdit',
    element: <FarmerGroupForm edit />,
    authorizedPermissions: ['farmer_group:edit'],
  },
  {
    path: routes.hotcash,
    key: 'hotcash',
    element: <Hotcash />,
    authorizedPermissions: ['collection:read'],
  },
  {
    path: routes.collectionOverview,
    key: 'collectionOverview',
    element: <CollectionDashboard />,
    authorizedPermissions: ['matrix:read'],
  },
  {
    path: routes.collectionInvoices,
    key: 'collectionInvoices',
    element: <InvoiceListPage />,
    authorizedPermissions: ['collection:read'],
  },
  {
    path: routes.paymentUtils,
    key: 'paymentUtils',
    element: <PaymentUtils />,
  },
  {
    path: routes.invoiceUtils,
    key: 'invoiceUtils',
    element: <InvoiceUtils />,
  },
  {
    path: routes.salesLeads,
    key: 'salesLeads',
    element: <SalesPipeline />,
    authorizedPermissions: ['sales:read'],
  },
  {
    path: routes.salesOverview,
    key: 'salesOverview',
    element: <SalesOverview />,
    authorizedPermissions: ['sales:read'],
  },
  {
    path: routes.salesCalendarView,
    key: 'salesCalendarView',
    element: <SalesDealsCalendarView />,
    authorizedPermissions: ['sales:read'],
  },
  {
    path: routes.salesAgData,
    key: 'salesAgData',
    element: <JrAgLeads />,
    authorizedPermissions: ['user:read'],
  },
  {
    path: routes.salesGroupProgress,
    key: 'salesGroupProgress',
    element: <SalesGroupProgress />,
    authorizedPermissions: ['sales:read'],
  },
  {
    path: routes.salesUtils,
    key: 'salesUtils',
    element: <SalesUtils />,
  },
  {
    path: routes.deliveryPackages,
    key: 'deliveryPackages',
    element: <PackageTracker />,
    authorizedPermissions: ['sales:read'],
  },
  {
    path: routes.adminReview,
    key: 'adminReview',
    element: <AdminReview />,
    authorizedPermissions: [
      'paddy_buyer:review',
      'farmer_group_leader:review',
      'machine_owner:review',
    ],
  },
];

type RoutePermission = {
  [K in keyof typeof routes]: PermissionKey[];
};

// This is used to map the route keys to their required permissions
export const routePermissions: RoutePermission = privateRoutes.reduce(
  (acc, route) => {
    acc[route.key] = route.authorizedPermissions || [];
    return acc;
  },
  {} as RoutePermission,
);

export const allRoutes = Object.entries(routes).map(([key, path]) => ({
  key,
  path,
}));
